import React, { useEffect, useState, memo, useCallback } from 'react';
import { Box, Tab, Tabs, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTabs } from './base/TabsProvider';

// Componente memoizado para cada pestaña
const TabPanel = memo(({ tab, isActive, stores, setHeaderForTab, setOptionsForTab }) => {
  // useCallback garantiza referencias estables: evita que cloneElement
  // propague re-renders al hijo cada vez que el padre se re-renderiza
  const setChildHeaderForThisTab = useCallback(
    (header) => setHeaderForTab(tab.path, header),
    [setHeaderForTab, tab.path]
  );
  const setOptionsForThisTab = useCallback(
    (options) => setOptionsForTab(tab.path, options),
    [setOptionsForTab, tab.path]
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        display: isActive ? 'block' : 'none'
      }}
    >
      {React.cloneElement(tab.component, {
        store: stores && stores[0],
        storeId: stores && stores[0]?._id,
        setOptions: setOptionsForThisTab,
        setChildHeader: setChildHeaderForThisTab
      })}
    </Box>
  );
});

TabPanel.displayName = 'TabPanel';

const TabsManager = ({ stores, setOptions, setChilds }) => {
  const { tabs, activeTab, closeTab, switchTab, tabHeaders, tabOptions, setHeaderForTab, setOptionsForTab } = useTabs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loadedTabs, setLoadedTabs] = useState(new Set());

  // Colores para las pestañas
  const tabColors = [
    '#E3F2FD', // Azul claro
    '#F3E5F5', // Púrpura claro
    '#E8F5E9', // Verde claro
    '#FFF3E0', // Naranja claro
    '#FCE4EC', // Rosa claro
    '#E0F2F1', // Turquesa claro
    '#FFF9C4', // Amarillo claro
    '#F1F8E9', // Lima claro
    '#E1F5FE', // Cian claro
    '#EDE7F6', // Violeta claro
  ];

  const getTabColor = (index) => tabColors[index % tabColors.length];

  // Marcar pestaña como cargada cuando se activa
  useEffect(() => {
    if (activeTab) {
      setLoadedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab]);

  const handleChange = (event, newValue) => {
    switchTab(newValue);
  };

  const handleClose = (event, path) => {
    event.stopPropagation();
    closeTab(path);
  };

  // Update global header when active tab changes
  useEffect(() => {
    if (activeTab && tabHeaders[activeTab]) {
      setChilds(tabHeaders[activeTab]);
    } else {
      setChilds(null);
    }
  }, [activeTab, tabHeaders, setChilds]);

  // Update global options when active tab changes
  useEffect(() => {
    if (activeTab && tabOptions[activeTab]) {
      setOptions(tabOptions[activeTab]);
    } else {
      setOptions([]);
    }
  }, [activeTab, tabOptions, setOptions]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            minHeight: 40,
            '& .MuiTab-root': {
              transition: isMobile ? 'none' : 'all 0.2s ease',
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.path}
              value={tab.path}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon && React.cloneElement(tab.icon, { sx: { fontSize: 18 } })}
                  <span>{tab.title}</span>
                  <IconButton
                    size="small"
                    onClick={(e) => handleClose(e, tab.path)}
                    sx={{ 
                      ml: 0.5, 
                      p: 0.5,
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              }
              sx={{ 
                minHeight: 40,
                textTransform: 'none',
                fontSize: '0.875rem',
                py: 0.5,
                bgcolor: getTabColor(index),
                borderRight: '1px solid',
                borderRightColor: 'divider',
                '&.Mui-selected': {
                  bgcolor: getTabColor(index),
                  fontWeight: 600,
                  boxShadow: 'inset 0 -3px 0 0 currentColor'
                },
                '&:hover': {
                  bgcolor: getTabColor(index),
                  filter: 'brightness(0.95)'
                }
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.path;
          const shouldRender = loadedTabs.has(tab.path);
          
          // Solo renderizar pestañas que ya han sido activadas al menos una vez
          if (!shouldRender) {
            return null;
          }

          return (
            <TabPanel
              key={tab.path}
              tab={tab}
              isActive={isActive}
              stores={stores}
              setHeaderForTab={setHeaderForTab}
              setOptionsForTab={setOptionsForTab}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default TabsManager;
